import { useEffect } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";

const faqsByLang: Record<string, { q: string; a: string }[]> = {
  de: [
    { q: "Ist CookieRobot vollständig DSGVO- und TTDSG-konform?", a: "Ja. CookieRobot protokolliert Einwilligungen rechtssicher per Hash und blockiert Cookies und Skripte automatisch, bevor eine ausdrückliche Einwilligung vorliegt. Wir erfüllen alle aktuellen Anforderungen der DSGVO, ePrivacy-Richtlinie und des TTDSG." },
    { q: "Wie lange dauert die Integration?", a: "Die Integration dauert in der Regel weniger als fünf Minuten. Sie fügen lediglich unser einzeiliges Skript in den <head>-Bereich Ihrer Website ein. Den Rest erledigt CookieRobot automatisch." },
    { q: "Funktioniert CookieRobot mit jeder Website?", a: "Ja. CookieRobot ist vollständig systemunabhängig und funktioniert mit jeder Website — unabhängig davon, mit welchem CMS, Baukasten, Shopsystem oder Framework sie erstellt wurde. Sie müssen lediglich eine Zeile HTML-Code einbinden können." },
    { q: "Muss ich Skripte manuell anpassen, z. B. Google Analytics?", a: "Nein. Dank unserer Auto-Blocking-Technologie erkennt und blockiert CookieRobot bekannte Tracking-Skripte automatisch, ohne dass Sie Ihren Website-Code anpassen müssen." },
    { q: "Kann ich mehrere Domains über ein Konto verwalten?", a: "Ja. Im Pro-Tarif können Sie beliebig viele Domains in einem Dashboard anlegen und verwalten. Ideal für Agenturen und Betreiber mehrerer Websites." },
    { q: "Erkennt das Banner die Sprache meiner Besucher?", a: "Ja. Das Banner erkennt automatisch die Browsersprache des Besuchers und zeigt den Text in einer der vier verfügbaren Sprachen (DE, EN, IT, FR)." },
    { q: "Wie funktioniert der automatische Cookie-Scanner?", a: "Unser Crawler durchsucht Ihre Website regelmäßig auf neue Cookies und kategorisiert sie anhand unserer Datenbank automatisch in Notwendig, Präferenzen, Statistik und Marketing." },
    { q: "Was passiert, wenn sich die Gesetzeslage ändert?", a: "Wir überwachen die aktuelle Rechtsprechung kontinuierlich. Bei Änderungen passen wir die Logik serverseitig an. Sie müssen auf Ihrer Website nichts aktualisieren." },
  ],
  en: [
    { q: "Is CookieRobot fully GDPR- and TTDSG-compliant?", a: "Yes. CookieRobot logs consents legally via hash and automatically blocks cookies and scripts before explicit user consent. We meet all current GDPR, ePrivacy, and TTDSG requirements." },
    { q: "How long does integration take?", a: "Integration usually takes less than five minutes. You simply add our one-line script to the <head> of your website. CookieRobot handles the rest." },
    { q: "Does CookieRobot work with any website?", a: "Yes. CookieRobot is completely system-agnostic and works with any website — no matter which CMS, site builder, shop system, or framework it was built with. You only need to be able to embed one line of HTML." },
    { q: "Do I need to manually adapt scripts like Google Analytics?", a: "No. Our auto-blocking technology recognizes and blocks known tracking scripts automatically — no code changes needed." },
    { q: "Can I manage multiple domains in one account?", a: "Yes. The Pro tier lets you add unlimited domains in one dashboard. Perfect for agencies and multi-site operators." },
    { q: "Does the banner detect my visitors' language?", a: "Yes. The banner auto-detects the browser language and displays text in one of four available languages (DE, EN, IT, FR)." },
    { q: "How does the automatic cookie scanner work?", a: "Our crawler regularly scans your site for new cookies and categorizes them via our database into Necessary, Preferences, Statistics, and Marketing." },
    { q: "What happens if the law changes?", a: "We continuously monitor current case law. When things change, we update the logic server-side. You don't need to touch your website." },
  ],
  it: [
    { q: "CookieRobot è pienamente conforme al GDPR e al TTDSG?", a: "Sì. CookieRobot registra i consensi legalmente tramite hash e blocca cookie e script automaticamente prima del consenso esplicito. Soddisfiamo tutti i requisiti attuali di GDPR, ePrivacy e TTDSG." },
    { q: "Quanto tempo richiede l'integrazione?", a: "L'integrazione richiede di solito meno di cinque minuti. Basta aggiungere il nostro script di una riga nel <head> del tuo sito. CookieRobot fa il resto." },
    { q: "CookieRobot funziona con qualsiasi sito web?", a: "Sì. CookieRobot è completamente indipendente dal sistema e funziona con qualsiasi sito web — indipendentemente dal CMS, builder, sistema e-commerce o framework utilizzato. È sufficiente poter inserire una riga di codice HTML." },
    { q: "Devo adattare manualmente script come Google Analytics?", a: "No. La nostra tecnologia di auto-blocking riconosce e blocca automaticamente gli script di tracking noti — senza modificare il codice." },
    { q: "Posso gestire più domini con un solo account?", a: "Sì. Il piano Pro permette di aggiungere domini illimitati in una sola dashboard. Ideale per agenzie." },
    { q: "Il banner rileva la lingua dei visitatori?", a: "Sì. Il banner rileva automaticamente la lingua del browser e mostra il testo in una delle quattro lingue disponibili (DE, EN, IT, FR)." },
    { q: "Come funziona lo scanner automatico dei cookie?", a: "Il nostro crawler analizza regolarmente il tuo sito per nuovi cookie e li categorizza tramite il nostro database in Necessari, Preferenze, Statistiche e Marketing." },
    { q: "Cosa succede se la legge cambia?", a: "Monitoriamo continuamente la giurisprudenza. Quando cambia, aggiorniamo la logica lato server. Non devi toccare il tuo sito." },
  ],
  fr: [
    { q: "CookieRobot est-il entièrement conforme RGPD et TTDSG ?", a: "Oui. CookieRobot journalise les consentements légalement via hash et bloque automatiquement cookies et scripts avant tout consentement explicite. Nous respectons toutes les exigences actuelles du RGPD, d'ePrivacy et du TTDSG." },
    { q: "Combien de temps prend l'intégration ?", a: "L'intégration prend généralement moins de cinq minutes. Il suffit d'ajouter notre script d'une ligne dans le <head> de votre site. CookieRobot s'occupe du reste." },
    { q: "CookieRobot fonctionne-t-il avec n'importe quel site web ?", a: "Oui. CookieRobot est totalement indépendant du système et fonctionne avec n'importe quel site web — quel que soit le CMS, le constructeur, le système e-commerce ou le framework utilisé. Il suffit de pouvoir intégrer une ligne de code HTML." },
    { q: "Dois-je adapter manuellement des scripts comme Google Analytics ?", a: "Non. Notre technologie d'auto-blocage reconnaît et bloque automatiquement les scripts de tracking connus — sans modifier le code." },
    { q: "Puis-je gérer plusieurs domaines avec un seul compte ?", a: "Oui. Le forfait Pro permet d'ajouter des domaines illimités dans un seul tableau de bord. Idéal pour les agences." },
    { q: "La bannière détecte-t-elle la langue des visiteurs ?", a: "Oui. La bannière détecte automatiquement la langue du navigateur et affiche le texte dans l'une des quatre langues disponibles (DE, EN, IT, FR)." },
    { q: "Comment fonctionne le scanner automatique de cookies ?", a: "Notre crawler analyse régulièrement votre site pour de nouveaux cookies et les catégorise via notre base de données en Nécessaires, Préférences, Statistiques et Marketing." },
    { q: "Que se passe-t-il si la loi change ?", a: "Nous surveillons en permanence la jurisprudence. Lors de changements, nous adaptons la logique côté serveur. Vous n'avez rien à faire sur votre site." },
  ],
};

export function FAQ() {
  const { t, language } = useTranslation();
  const faqs = faqsByLang[language] ?? faqsByLang.de;

  useEffect(() => {
    const id = "faq-jsonld";
    const json = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(json);
    return () => {
      const e = document.getElementById(id);
      if (e) e.remove();
    };
  }, [faqs]);

  // split FAQs into two columns for full-width layout
  const mid = Math.ceil(faqs.length / 2);
  const colA = faqs.slice(0, mid);
  const colB = faqs.slice(mid);

  return (
    <section id="faq" className="py-20 md:py-28 scroll-mt-16">
      <div className="container mx-auto max-w-[1440px] px-5 md:px-6">
        <div className="grid lg:grid-cols-[360px_1fr] gap-10 lg:gap-16">
          {/* LEFT — sticky intro / contact panel */}
          <Reveal>
            <div className="lg:sticky lg:top-24">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                FAQ
              </div>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                {t("faq.title")}
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                {t("faq.subtitle")}
              </p>

              {/* secondary stats / hint */}
              <div className="mt-8 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <HelpCircle className="w-3.5 h-3.5 text-primary/70" />
                  {faqs.length} häufige Fragen beantwortet
                </span>
              </div>
            </div>
          </Reveal>

          {/* RIGHT — two-column accordion */}
          <Reveal delay={80}>
            <div className="grid md:grid-cols-2 gap-x-10">
              {[colA, colB].map((col, ci) => (
                <Accordion key={ci} type="single" collapsible className="w-full">
                  {col.map((faq, i) => (
                    <AccordionItem
                      key={i}
                      value={`item-${ci}-${i}`}
                      className="border-b border-border last:border-b-0"
                    >
                      <AccordionTrigger className="text-left font-medium text-[0.98rem] py-5 hover:no-underline">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed text-[0.92rem] pb-5">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
